import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { combineLatest, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';
import * as pluralize from 'pluralize';
import { LoadPatientData, LoginPatient, UpdatePatient } from '../core/store/patient/patient.actions';
import { EnvService, SessionExpirationService, SessionExpirationStatus } from '../core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { validateAllFormFields } from '../core/utils/validators';
import { LoadLookupData } from '../core/store/lookup/lookup.actions';
import { LookupState } from '../core/store/lookup/lookup.state';
import { AddressOrders, AuthenticatedUser, InsuranceTypeReportGroups, Patient } from '../core/models';
import { Gtag } from 'angular-gtag';
import { ApiStoreData, CcsSentryError } from '../core/store/utils';
import { UserState } from '../core/store/user/user.state';
import { AddressesState } from '../core/store/addresses/addresses.state';
import { MessageService } from 'primeng/api';
import { FetchPortalUser, SignOut } from '../core/store/user/user.actions';
import { PatientState } from '../core/store/patient/patient.state';
import { omit } from 'lodash-es';
import { ClearPreviousShippingAddressOrders } from '../core/store/addresses/addresses.actions';
import { asFormControl } from '../core/utils/common';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  @Select(UserState.authenticatedUser) authenticatedUser$!: Observable<AuthenticatedUser | null>;
  @Select(UserState.patientSelectionRequired) patientSelectionRequired$!: Observable<boolean>;
  @Select(PatientState.isPreviewMode) isPreviewMode$!: Observable<boolean>;
  @Select(PatientState.patient) patient$!: Observable<Patient | null>;
  @Select(PatientState.patientStateErrors) patientStateErrors$!: Observable<CcsSentryError[] | null>;
  @Select(PatientState.patientStateIsLoading) patientStateIsLoading$!: Observable<boolean>;
  @Select(PatientState.patientsState) patientsState$!: Observable<ApiStoreData<Patient[]>>;
  @Select(PatientState.selectedPatientId) selectedPatientId$!: Observable<number | null>;
  @Select(PatientState.updatePatientState) updatePatientState$!: Observable<ApiStoreData<null>>;
  @Select(LookupState.insuranceGroups) insuranceGroups$!: Observable<InsuranceTypeReportGroups[]>;
  @Select(UserState.isAgentUser) isAgentUser$!: Observable<boolean>;
  @Select(AddressesState.addressOrdersState) addressOrdersState$!: Observable<ApiStoreData<AddressOrders | null>>;

  displaySessionExpirationModal: boolean = false;
  sessionExpirationTimeLeft: string = '';
  displaySelectPatientModal: boolean = false;
  displayAcceptTermsModal: boolean = false;
  displayHipaaVerifyModal: boolean = false;
  displayBetaBanner: boolean = false;
  displayAddressOrdersModal: boolean = false;
  excludeAddressChangeReorderId?: number;

  termsAndConditionsForm = new FormGroup({
    acceptedTermsAndConditions: new FormControl(false, Validators.requiredTrue),
  });

  selectPatientForm = new FormGroup({
    selectedPatientId: new FormControl(null),
  });

  termsAndConditionsUrl = this.envService.ccs.termsAndConditionsUrl;
  privacyUrl = this.envService.ccs.privacyUrl;
  showTermsAndConditionsError: boolean = false;

  asFormControl = asFormControl;

  constructor(
    private store: Store,
    private sessionExpirationService: SessionExpirationService,
    private router: Router,
    private envService: EnvService,
    private gtag: Gtag,
    private route: ActivatedRoute,
    private messageService: MessageService
  ) {
    this.sessionExpirationTimeLeft = `less than ${pluralize(
      'minute',
      this.sessionExpirationService.WARNING_TIMER_VALUE_IN_MIN,
      true
    )}`;
  }

  ngOnInit(): void {
    this.initSessionExpirationTimer();
    this.store.dispatch(new FetchPortalUser());

    this.patientSelectionRequired$.subscribe((patientSelectionRequired) => {
      if (patientSelectionRequired) {
        this.router.navigate(['/']);
        this.showSelectPatientModal();
      }
    });

    this.patient$.subscribe((patient) => {
      if (!!patient && (!patient.acceptedTermsAndConditions || !patient.verifiedHipaa)) {
        if (this.router.url !== '/') {
          this.router.navigate(['/']);
        }

        if (!patient.verifiedHipaa) {
          this.showHipaaVerifyModal();
        } else {
          this.showAcceptTermsModal();
        }
      }
    });

    if (!this.envService.features['notifications']?.enabled) {
      setTimeout(() => {
        this.showBetaBanner();
      }, 1000);
    }

    this.initFetchingBackgroundData();

    this.route.queryParams.subscribe((qp) => {
      const checkAddressChangesKey = 'checkAddressChanges';
      const excludeAddressChangeReorderIdKey = 'excludeAddressChangeReorderId';

      if (qp[checkAddressChangesKey] === 'true') {
        const terminate = new Subject<void>();

        this.addressOrdersState$.pipe(takeUntil(terminate)).subscribe((addressOrdersState) => {
          if (!addressOrdersState.isLoading) {
            if (addressOrdersState.data?.orders.length) {
              this.excludeAddressChangeReorderId = qp[excludeAddressChangeReorderIdKey]
                ? parseInt(qp[excludeAddressChangeReorderIdKey]!, 10)
                : undefined;

              const filteredOrders = addressOrdersState.data.orders.filter(
                (o) => o.id !== this.excludeAddressChangeReorderId
              );

              if (filteredOrders.length) {
                this.showAddressOrdersModal();
              } else {
                this.store.dispatch(new ClearPreviousShippingAddressOrders());
              }
            }
            terminate.next();
          }
        });

        let newQueryParams = omit(qp, [checkAddressChangesKey, excludeAddressChangeReorderIdKey]);

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: newQueryParams,
        });
      }
    });
  }

  initFetchingBackgroundData() {
    const startedFetchingLookupData = new Subject<void>();
    this.patientsState$.pipe(takeUntil(startedFetchingLookupData)).subscribe((patientsState) => {
      if (patientsState.data.length > 0) {
        this.store.dispatch(new LoadLookupData());
        startedFetchingLookupData.next();
      }
    });

    const startedFetchingPatientData = new Subject<void>();

    combineLatest([this.selectedPatientId$, this.insuranceGroups$])
      .pipe(takeUntil(startedFetchingPatientData))
      .subscribe(([selectedPatientId, insuranceGroups]) => {
        if (!!selectedPatientId && insuranceGroups.length > 0) {
          this.store.dispatch(new LoadPatientData());
          startedFetchingPatientData.next();
        }
      });
  }

  initSessionExpirationTimer() {
    // Watcher on timer
    this.sessionExpirationService.initilizeSessionTimeout();
    if (this.sessionExpirationService.userIdlenessChecker) {
      this.sessionExpirationService.userIdlenessChecker.subscribe((status: string) => {
        if (status === SessionExpirationStatus.StopExpirationTimer) {
          this.showSessionExpirationModal();
        }
      });
    }

    if (this.sessionExpirationService.idlenessWarningChecker) {
      this.sessionExpirationService.idlenessWarningChecker.subscribe((status: string) => {
        if (status === SessionExpirationStatus.StopWarningTimer) {
          this.terminateSession();
        }
      });
    }
  }

  continueSession() {
    this.hideSessionExpirationModal();

    // stop second timer and initiate first timer again
    SessionExpirationService.runWarningTimer = false;
    this.sessionExpirationService.initilizeSessionTimeout();

    this.gtag.event('session_expiration_continue_session', {
      event_category: 'engagement',
    });
  }

  terminateSession() {
    SessionExpirationService.runTimer = false;
    SessionExpirationService.runWarningTimer = false;
    setTimeout(() => {
      this.store.dispatch(new SignOut({ sessionExpired: true })).subscribe(() => {
        this.hideSessionExpirationModal();
      });
    }, 500);

    this.gtag.event('session_expiration_automatic_logout', {
      event_category: 'engagement',
      non_interaction: true,
    });
  }

  changeSelectedPatient(pId: number) {
    this.selectPatientForm.patchValue({ selectedPatientId: pId });
  }

  loginPatient(pId: number) {
    this.store.dispatch(new LoginPatient(this.store.selectSnapshot(UserState.ipAddress) || 'Unknown', pId));
  }

  onSubmitSelectPatient() {
    if (this.selectPatientForm.valid) {
      const selectedPatientId: number = this.selectPatientForm.get('selectedPatientId')?.value;
      this.loginPatient(selectedPatientId);
      this.hideSelectPatientModal();
    } else {
      validateAllFormFields(this.selectPatientForm);
    }
  }

  onSubmitTermsAndConditions() {
    if (this.termsAndConditionsForm.valid) {
      this.store
        .dispatch(
          new UpdatePatient({
            acceptedTermsAndConditions: this.termsAndConditionsForm.value.acceptedTermsAndConditions,
          })
        )
        .pipe(withLatestFrom(this.updatePatientState$))
        .subscribe(([_, updatePatientState]) => {
          if (updatePatientState.error) {
            this.showTermsAndConditionsError = true;
            return;
          }

          this.hideAcceptTermsModal();
          this.showTermsAndConditionsError = false;
          this.termsAndConditionsForm.reset();
        });
    } else {
      validateAllFormFields(this.termsAndConditionsForm);
    }
  }

  onAddressOrdersUpdated() {
    this.messageService.add({
      key: 'homeToast',
      severity: 'success',
      detail: 'Successfully updated your order addresses.',
    });
  }

  showSessionExpirationModal() {
    this.displaySessionExpirationModal = true;
    this.gtag.event('session_expiration_modal_shown', {
      event_category: 'engagement',
      non_interaction: true,
    });
  }

  hideSessionExpirationModal() {
    this.displaySessionExpirationModal = false;
  }

  showSelectPatientModal() {
    this.displaySelectPatientModal = true;
  }

  hideSelectPatientModal() {
    this.displaySelectPatientModal = false;
  }

  showHipaaVerifyModal() {
    this.displayHipaaVerifyModal = true;
  }

  hideHipaaVerifyModal() {
    this.displayHipaaVerifyModal = false;
  }

  showAcceptTermsModal() {
    this.showTermsAndConditionsError = false;
    this.displayAcceptTermsModal = true;
  }

  hideAcceptTermsModal() {
    this.displayAcceptTermsModal = false;
  }

  showBetaBanner() {
    this.displayBetaBanner = true;
  }

  hideBetaBanner() {
    this.displayBetaBanner = false;
  }

  showAddressOrdersModal() {
    this.displayAddressOrdersModal = true;
  }

  hideAddressOrdersModal() {
    this.displayAddressOrdersModal = false;
  }

  ngOnDestroy() {
    SessionExpirationService.runTimer = false;
    SessionExpirationService.runWarningTimer = false;
  }
}
